import { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import tw, { TwStyle } from 'twin.macro';

import { Dropdown, IDropdownOption } from '@components/Dropdown';
import { InviteForm } from '@account_module/components/InviteForm';
import { SearchBar } from '@account_module/components/SearchBar';
import { RoleSelect, OptionType } from '@account_module/components/RoleSelect';
import { useAccountSettingContext } from '@account_module/context/AccountSettingsContext';
import { useOrganizations } from '@account_module/hooks/useOrganizations';
import { useOrganization } from '@account_module/hooks/useOrganization';
import { useOrganizationUsers } from '@account_module/hooks/useOrganizationUsers';
import { useNotification } from '@insights_module/hooks/useNotification';
import { updateUser } from '@account_module/services';
import { ORGANIZATION_QUERY_KEY } from '@account_module/constants/queryKeys';
import { UserRoleType } from '@account_module/types/Organizations';
import { USER_ROLE_KEY, USER_STATUS_KEY } from '@account_module/constants/userKeys';
import { CommonResponse } from '@auth_module/services';
import { Loader } from '@components/Loader';
import { SidebarToggleButton } from '@insights_module/components/SidebarToggleButton';
import { AudioProcessingUsage } from '@account_module/components/AudioProcessingReport';
import { useFeatureFlag, FEATURE_FLAG } from '@auth_module/hooks/useFeatureFlag';

const Container = tw.div`h-[41px] w-full px-6 py-[5px] flex items-center justify-between bg-neutral-background border-b border-solid border-gray-3`;
const InputsWrapper = tw.div`flex items-center justify-start gap-2`;
const Flex = tw.div`flex items-center justify-start gap-2`;
const FlexStart = tw(Flex)`gap-0`;
const FlexEnd = tw(Flex)`justify-end gap-0`;
const Option = tw.span`w-auto p-1 text-[11px] cursor-pointer whitespace-nowrap flex items-center justify-between hover:bg-gray-3 rounded-[3px]`;
const UsersCountCopy = tw.span`text-[11px] font-medium text-text-and-icon-muted-2 whitespace-nowrap mr-2`;
const ControllerButton = tw.button`rounded-[4px] min-w-[75px] w-auto bg-gray-3 text-text-and-icon h-6 text-[11px] flex flex-row items-center justify-center border border-solid px-[10px] whitespace-nowrap transition-all duration-100 not-last:mr-2 active:opacity-70`;
const labelStyle =
  tw`border-none shadow-none text-[11px] text-text-and-icon bg-neutral-background hover:bg-gray-3 pl-1 pr-0` as TwStyle;
const selectLabelStyle =
  tw`bg-gray-3 border-none text-text-and-icon h-6 text-[11px] flex flex-row items-center justify-between px-[10px] whitespace-nowrap transition-all duration-100 mr-2 active:opacity-70` as TwStyle;
const dropdownMenuStyle =
  tw`p-1 w-[190px] top-8 z-30 bg-neutral-background text-text-and-icon` as TwStyle;

const options: OptionType[] = [
  {
    key: USER_ROLE_KEY.editor,
    label: 'User',
    title: 'User',
    description: 'Can see Audio Insights analysis, create views and export content',
  },
  {
    key: USER_ROLE_KEY.admin,
    label: 'Admin',
    title: 'Admin',
    description: 'Can do everything as user can and can manage content and team',
  },
  {
    key: USER_ROLE_KEY.viewer,
    label: 'Viewer',
    title: 'Viewer',
    description: 'Can see Audio Insights analysis',
  },
];

const roleOptions: IDropdownOption[] = [
  {
    label: (
      <Option>
        <Trans
          i18nKey={'account_module.settings_page.users_table_controler.role_dropdown.option_all'}>
          All
        </Trans>
      </Option>
    ),
    value: 'all',
    hasDivider: true,
  },
  {
    label: (
      <Option>
        <Trans
          i18nKey={'account_module.settings_page.users_table_controler.role_dropdown.option_admin'}>
          Admin
        </Trans>
      </Option>
    ),
    value: USER_ROLE_KEY.admin,
  },
  {
    label: (
      <Option>
        <Trans
          i18nKey={'account_module.settings_page.users_table_controler.role_dropdown.option_user'}>
          Editor
        </Trans>
      </Option>
    ),
    value: USER_ROLE_KEY.editor,
  },
  {
    label: (
      <Option>
        <Trans
          i18nKey={
            'account_module.settings_page.users_table_controler.role_dropdown.option_viewer'
          }>
          Viewer
        </Trans>
      </Option>
    ),
    value: USER_ROLE_KEY.viewer,
  },
];

const statusOptions: IDropdownOption[] = [
  {
    label: (
      <Option>
        <Trans
          i18nKey={'account_module.settings_page.users_table_controler.status_dropdown.option_all'}>
          All
        </Trans>
      </Option>
    ),
    value: 'all',
    hasDivider: true,
  },
  {
    label: (
      <Option>
        <Trans
          i18nKey={
            'account_module.settings_page.users_table_controler.status_dropdown.option_activated'
          }>
          Activated
        </Trans>
      </Option>
    ),
    value: USER_STATUS_KEY.activated,
  },
  {
    label: (
      <Option>
        <Trans
          i18nKey={
            'account_module.settings_page.users_table_controler.status_dropdown.option_invited'
          }>
          Invited
        </Trans>
      </Option>
    ),
    value: USER_STATUS_KEY.invited,
  },
];

export const UsersTableController = (): JSX.Element => {
  const { t } = useTranslation();
  const { notify, NTFC_TYPE } = useNotification();
  const containerRef = useRef(null);
  const { getOrganizationByIndex } = useOrganizations();
  const {
    setActiveStatus,
    setActiveRole,
    selectedUsers,
    setSelectedUsers,
    setIsRemoveUserModalActive,
    query,
    setQuery,
    activeRole,
    activeStatus,
  } = useAccountSettingContext();
  const queryClient = useQueryClient();
  const organization = getOrganizationByIndex(0);
  const { data: users, currentUser } = useOrganizationUsers(organization?.id);
  const { isUpdateLoading } = useOrganization(organization?.id || '');
  const [isUpdateUsersLoading, setIsUpdateUsersLoading] = useState<boolean>(false);
  const { isEnabled: isAudioProcessingCountEnabled } = useFeatureFlag(
    FEATURE_FLAG.AUDIO_PROCESSING_COUNT
  );

  const isBulk = useMemo(() => selectedUsers.length > 0, [selectedUsers]);

  const currentActiveRole = useMemo(
    () => roleOptions.find((item) => item.value === activeRole),
    [activeRole, roleOptions]
  );

  const currentActiveStatus = useMemo(
    () => statusOptions.find((item) => item.value === activeStatus),
    [activeStatus, roleOptions]
  );

  const handleCopyEmails = () => {
    const emails: string = users
      ?.filter((user) => selectedUsers.includes(user.id))
      .map((user) => user.email)
      .join() as string;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(emails);
      notify({
        type: NTFC_TYPE.INFO,
        message: t(
          'account_module.settings_page.users_table_controler.notifications.emails_copied',
          { count: selectedUsers.length }
        ),
      });
    } else {
      console.error('Copy to clipboard not supported');
    }
  };

  const handleUpdateUsers = async (val: UserRoleType) => {
    if (isUpdateLoading) return;

    // Filter users who already have a selected role, so that we do not make redundant requests and display error messages.
    const filteredUsers: string[] | undefined = users
      ?.filter((user) => selectedUsers.includes(user.id) && user.role !== val)
      .map((user) => user.id);

    setIsUpdateUsersLoading(true);

    if (!filteredUsers?.length) {
      setIsUpdateUsersLoading(false);
      notify({
        type: NTFC_TYPE.INFO,
        message: t(
          'account_module.settings_page.users_table_controler.notifications.users_updated_success',
          { count: filteredUsers?.length }
        ),
      });
      return;
    }

    const requests: Promise<CommonResponse | undefined>[] = filteredUsers.map(async (user) => {
      return await updateUser(organization?.id, user, { permission_type: val });
    });

    Promise.all(requests)
      .then(() => {
        notify({
          type: NTFC_TYPE.INFO,
          message: t(
            'account_module.settings_page.users_table_controler.notifications.users_updated_success',
            { count: filteredUsers.length }
          ),
        });
        setSelectedUsers([]);
      })
      .catch((error) => {
        notify({
          type: NTFC_TYPE.WARNING,
          message: t(
            'account_module.settings_page.users_table_controler.notifications.users_updated_error',
            { message: error.message }
          ),
        });
      })
      .finally(() => {
        queryClient.invalidateQueries([ORGANIZATION_QUERY_KEY.users]);
        queryClient.invalidateQueries([ORGANIZATION_QUERY_KEY.organization]);
        setIsUpdateUsersLoading(false);
      });
  };

  return (
    <>
      {isAudioProcessingCountEnabled && (
        <div tw='w-full px-6 py-[5px]'>
          <AudioProcessingUsage />
        </div>
      )}
      <Container ref={containerRef}>
        <InputsWrapper>
          <SidebarToggleButton mode='close' />
          <Flex tw='gap-0 w-auto'>
            <Dropdown
              id='role-dropdown'
              value={currentActiveRole ?? roleOptions[0]}
              onChange={(val) => {
                const { value } = val as IDropdownOption;
                setActiveRole(value as keyof IDropdownOption);
              }}
              options={roleOptions}
              labelStyle={labelStyle}
              dropdownMenuStyle={dropdownMenuStyle}
              labelPrefix={t(
                'account_module.settings_page.users_table_controler.role_dropdown.title'
              )}
            />
          </Flex>
          <Flex tw='gap-0 w-auto'>
            <Dropdown
              id='status-dropdown'
              value={currentActiveStatus ?? statusOptions[0]}
              onChange={(val) => {
                const { value } = val as IDropdownOption;
                setActiveStatus(value as keyof IDropdownOption);
              }}
              options={statusOptions}
              labelStyle={labelStyle}
              dropdownMenuStyle={dropdownMenuStyle}
              labelPrefix={t(
                'account_module.settings_page.users_table_controler.status_dropdown.title'
              )}
            />
          </Flex>

          <Flex>
            <SearchBar
              tw='h-[28px] w-[326px] text-sm placeholder:text-xs'
              value={query}
              onChange={(val) => setQuery(val as string)}
              placeholder={t('account_module.settings_page.header.search_placeholder')}
            />
          </Flex>
        </InputsWrapper>

        {currentUser.isAdmin && !selectedUsers.includes(currentUser?.id as string) ? (
          <FlexEnd>
            {selectedUsers.length > 0 && (
              <UsersCountCopy>
                <Trans
                  i18nKey='account_module.settings_page.users_table_controler.selected_users_count'
                  values={{ count: selectedUsers.length }}>
                  <span>{selectedUsers.length}</span> members selected:
                </Trans>
              </UsersCountCopy>
            )}
            {isBulk && (
              <FlexStart>
                {isUpdateUsersLoading ? (
                  <ControllerButton tw='min-w-[75px]'>
                    <Loader theme='dark' size='xs' />
                  </ControllerButton>
                ) : (
                  <RoleSelect
                    options={options}
                    staticLabel='Change Role'
                    labelStyle={selectLabelStyle}
                    onChange={(val) => handleUpdateUsers(val as UserRoleType)}
                  />
                )}
                <ControllerButton onClick={handleCopyEmails}>
                  {t('account_module.settings_page.users_table_controler.bulk_cta.copy_emails')}
                </ControllerButton>
                <ControllerButton
                  tw='text-error-text-icon bg-error-bg border-error-bg'
                  onClick={() => setIsRemoveUserModalActive(true)}>
                  {t('account_module.settings_page.users_table_controler.bulk_cta.remove_members')}
                </ControllerButton>
                <div tw='w-[1px] h-5 mr-2 bg-gray-1' />
              </FlexStart>
            )}
            <InviteForm organizationId={organization?.id} />
          </FlexEnd>
        ) : (
          isBulk && (
            <ControllerButton tw='w-auto' onClick={handleCopyEmails}>
              {t('account_module.settings_page.users_table_controler.bulk_cta.copy_emails')}
            </ControllerButton>
          )
        )}
      </Container>
    </>
  );
};
